<template>
  <div v-for="(itemGroup, index) in renderedGroups" :key="index">
    <div
      data-testid="sidebar-nav-group"
      role="group"
      :aria-label="itemGroup.name"
    >
      <!-- Section header: border always visible, text fades via Motion -->
      <div
        v-if="itemGroup.name"
        :id="`sidebar-section-${index}`"
        :class="{
          'pb-[6px]': sidebar.open,
          'pt-[6px]': sidebar.open && index === 0,
          'border-t border-neutral-border-default mt-s pt-s': index > 0,
        }"
        class="px-xs overflow-hidden"
      >
        <Motion.span
          :variants="sectionHeaderVariants"
          data-testid="sidebar-section-header"
          class="text-body-small text-neutral-text-caption whitespace-nowrap block"
          :aria-hidden="!sidebar.open"
        >
          {{ itemGroup.name }}
        </Motion.span>
      </div>

      <!-- Navigation items (non-Manage or all items for Manage) -->
      <template v-if="!isManageSection(itemGroup)">
        <ul class="space-y-[2px] list-none m-0 p-0" role="list">
          <li
            v-for="navigationItem in getAuthorizedItems(itemGroup)"
            :key="navigationItem.name"
          >
            <SidebarNavigationItem
              :item="navigationItem"
              :type="itemGroup.position"
              :badge="navigationItem?.badge ? navigationItem.badge : undefined"
              class="w-full"
            />
          </li>
        </ul>
      </template>

      <!-- Manage section: split into always-visible + animated overflow -->
      <template v-else>
        <!-- Always-visible items (first 3) -->
        <ul class="space-y-[2px] list-none m-0 p-0" role="list">
          <li
            v-for="navigationItem in getBaseItems(itemGroup)"
            :key="navigationItem.name"
          >
            <SidebarNavigationItem
              :item="navigationItem"
              :type="itemGroup.position"
              :badge="navigationItem?.badge ? navigationItem.badge : undefined"
              class="w-full"
            />
          </li>
        </ul>

        <!-- Overflow items: spring-driven height + opacity -->
        <Motion.div
          v-if="hasHiddenItems(itemGroup)"
          :initial="false"
          :animate="{ height: manageExpanded ? 'auto' : 0 }"
          :transition="containerTransition"
          class="overflow-hidden"
          data-testid="sidebar-overflow-container"
          :aria-hidden="!manageExpanded"
        >
          <ul class="space-y-[2px] pt-[2px] list-none m-0 p-0" role="list">
            <Motion.li
              v-for="(navigationItem, itemIndex) in getOverflowItems(itemGroup)"
              :key="navigationItem.name"
              :initial="false"
              :animate="{
                opacity: manageExpanded ? 1 : 0,
                y: manageExpanded ? 0 : -8,
              }"
              :transition="getItemTransition(itemIndex)"
            >
              <SidebarNavigationItem
                :item="navigationItem"
                :type="itemGroup.position"
                :badge="navigationItem?.badge ? navigationItem.badge : undefined"
                class="w-full"
                :tabindex="manageExpanded ? undefined : -1"
              />
            </Motion.li>
          </ul>
        </Motion.div>

        <!-- Show more/less toggle -->
        <button
          v-if="hasHiddenItems(itemGroup)"
          data-testid="sidebar-show-more-toggle"
          type="button"
          :aria-expanded="manageExpanded"
          class="flex flex-wrap items-center gap-3xs cursor-pointer transition-colors px-xs py-[6px] text-neutral-text-subtitle hover:text-neutral-text-body w-full bg-transparent border-0"
          :class="[
            sidebar.open ? '' : 'justify-center',
          ]"
          @click="toggleManageExpanded"
        >
          <Motion.span
            :variants="showMoreLessVariants"
            class="text-body-base"
            :aria-hidden="!sidebar.open"
          >
            {{ manageExpanded ? $t('app.sidebar.show_less') : $t('app.sidebar.show_more') }}
          </Motion.span>
          <MeridiaIcon
            aria-hidden="true"
            name="keyboard_arrow_down"
            type="material-symbols-outlined"
            class="icon-s transition-transform duration-300 ease-out"
            :class="[manageExpanded ? 'rotate-180' : '']"
          />
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAppStore } from 'src/core/stores/app';
import { NavigationItem, NavigationItemGroup } from 'src/core/types/app';
import { useLocalStorage } from '@vueuse/core';
import { Motion } from 'src/core/composables/useMotion';
import SidebarNavigationItem from './SidebarNavigationItem.vue';
import MeridiaIcon from 'src/design_system/components/icon/MeridiaIcon.vue';

const props = defineProps<{
  mobile: boolean;
  navigationItemsGroup: NavigationItemGroup[];
  badge?: string;
}>();

const appStore = useAppStore();
const sidebar = useLocalStorage('sidebar', { open: true });
const manageExpanded = ref(false);

const sectionHeaderVariants = {
  expanded: {
    opacity: 1,
    height: 14,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
  collapsed: {
    opacity: 0,
    height: 0,
    y: -30,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
};

const showMoreLessVariants = {
  expanded: {
    opacity: 1,
    height: 16,
    x: 0,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
  collapsed: {
    opacity: 0,
    height: 0,
    x: -10,
    transition: { type: 'spring' as const, stiffness: 300, damping: 25 },
  },
};

// Container: critically damped spring (no bounce on height)
// On collapse, delays 50ms so children fade before container shrinks
const containerTransition = computed(() => ({
  type: 'spring' as const,
  stiffness: 500,
  damping: 40,
  ...(manageExpanded.value ? {} : { delay: 0.05 }),
}));

// Groups with no renderable item are skipped; index (and so the separator)
// follows the rendered order, not the array order.
const renderedGroups = computed(() =>
  props.navigationItemsGroup.filter(hasAuthorizedItems)
);

// Per-item spring with subtle 20ms stagger on expand, instant on collapse
function getItemTransition(index: number) {
  return {
    type: 'spring' as const,
    stiffness: 400,
    damping: 30,
    ...(manageExpanded.value ? { delay: index * 0.02 } : {}),
  };
}

function isItemAuthorized(item: NavigationItem): boolean {
  return 'route' in item ? appStore.isRouteAuthorized(item.route.name) : true;
}

function isItemVisible(item: NavigationItem): boolean {
  return item.isVisible?.() ?? true;
}

function isItemRenderable(item: NavigationItem): boolean {
  return isItemAuthorized(item) && isItemVisible(item);
}

function hasAuthorizedItems(group: NavigationItemGroup): boolean {
  return group.items.some((item) => isItemRenderable(item));
}

function isManageSection(group: NavigationItemGroup): boolean {
  return group.name === 'Manage';
}

function getAuthorizedItems(group: NavigationItemGroup): NavigationItem[] {
  return group.items.filter((item) => isItemRenderable(item));
}

function getBaseItems(group: NavigationItemGroup): NavigationItem[] {
  const authorized = getAuthorizedItems(group);
  return authorized.length > 3 ? authorized.slice(0, 3) : authorized;
}

function getOverflowItems(group: NavigationItemGroup): NavigationItem[] {
  const authorized = getAuthorizedItems(group);
  return authorized.length > 3 ? authorized.slice(3) : [];
}

function hasHiddenItems(group: NavigationItemGroup): boolean {
  return isManageSection(group) && getAuthorizedItems(group).length > 3;
}

function toggleManageExpanded(event: Event) {
  manageExpanded.value = !manageExpanded.value;
  if (manageExpanded.value) {
    const button = event.currentTarget as HTMLElement;
    setTimeout(() => {
      button?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }, 300);
  }
}
</script>
